import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { CircleCheck, CircleAlert, ExternalLink } from 'lucide-react';
import { fmtCurrency, fmtDate } from './helpers';

const ChallanTab = ({ loading, report, activeFY, openBillDetail }) => {
  return (
    <>
      {loading && <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600"></div></div>}
      {report && !loading && (() => {
        const challanEntries = report.entries.map(e => {
          const chBills = e.bills.filter(b => b.side === 'debit' && b.bill_type === 'challan');
          const chAmt = chBills.reduce((s, b) => s + (b.amount || 0), 0);
          return { ...e, bills: chBills, net_sales: chAmt, total_commission: 0 };
        }).filter(e => e.bills.length > 0);
        const totalChallanAmt = challanEntries.reduce((s, e) => s + e.net_sales, 0);
        const totalChallanCount = challanEntries.reduce((s, e) => s + e.bills.length, 0);
        return (
          <>
            {/* Challan Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Challan Entries</p><p className="text-lg font-bold text-amber-700">{totalChallanCount}</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500">Total Challan Amount</p><p className="text-lg font-bold text-amber-700" data-testid="challan-total-amt">{fmtCurrency(totalChallanAmt)}</p></CardContent></Card>
              <Card className="bg-amber-50 border-amber-200"><CardContent className="p-4 text-center"><p className="text-xs text-amber-600 font-medium">Commission</p><p className="text-lg font-bold text-gray-400">N/A</p><p className="text-[10px] text-amber-500">No commission on challans</p></CardContent></Card>
            </div>

            {challanEntries.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-gray-400">No challan entries found in {activeFY.label}</CardContent></Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="px-4 py-2 bg-amber-50 border-b text-xs text-amber-700 font-medium">{totalChallanCount} challan entries</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="challan-table">
                      <thead><tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-3 py-3 text-left font-medium w-[180px]">Customer / Distributor</th>
                        <th className="px-2 py-3 text-center font-medium w-[40px]">Type</th>
                        <th className="px-3 py-3 text-left font-medium">Bill #</th>
                        <th className="px-3 py-3 text-left font-medium">Inv/CN #</th>
                        <th className="px-2 py-3 text-left font-medium">Date</th>
                        <th className="px-3 py-3 text-right font-medium">Amount</th>
                        <th className="px-2 py-3 text-center font-medium">Status</th>
                      </tr></thead>
                      <tbody>
                        {challanEntries.map(entry => (
                          <React.Fragment key={entry.entity_id}>
                            <tr className="bg-amber-50/60 border-t-2 border-amber-200">
                              <td className="px-3 py-2 font-bold text-gray-800">{entry.entity_name} <Badge className="text-[9px] bg-gray-200 text-gray-600">{entry.entity_type}</Badge></td>
                              <td colSpan={4} className="px-3 py-2 text-xs text-gray-500">{entry.bills.length} challan(s)</td>
                              <td className="px-3 py-2 text-right text-xs font-bold text-amber-700">{fmtCurrency(entry.net_sales)}</td>
                              <td></td>
                            </tr>
                            {entry.bills.map(b => (
                              <tr key={b.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`challan-row-${b.id}`}>
                                <td className="px-3 py-2"></td>
                                <td className="px-2 py-2 text-center"><Badge className="text-[10px] bg-amber-100 text-amber-700">SE (CH)</Badge></td>
                                <td className="px-3 py-2 font-mono text-xs font-medium text-amber-700">
                                  <button className="hover:underline cursor-pointer flex items-center gap-1" onClick={() => openBillDetail(b)}>
                                    {b.number} <ExternalLink className="w-3 h-3 text-gray-400" />
                                  </button>
                                </td>
                                <td className="px-3 py-2 text-xs text-gray-500">{b.invoice_number || '-'}</td>
                                <td className="px-2 py-2 text-xs text-gray-600 whitespace-nowrap">{fmtDate(b.date)}</td>
                                <td className="px-3 py-2 text-right text-xs font-medium">{fmtCurrency(b.amount)}</td>
                                <td className="px-2 py-2 text-center">{b.is_billed ? <CircleCheck className="w-4 h-4 text-green-600 inline" /> : <CircleAlert className="w-4 h-4 text-yellow-500 inline" />}</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                        <tr className="bg-amber-100 border-t-2 border-amber-300">
                          <td colSpan={5} className="px-3 py-3 text-right font-bold text-sm text-amber-900">Grand Total</td>
                          <td className="px-3 py-3 text-right text-sm font-bold text-amber-900">{fmtCurrency(totalChallanAmt)}</td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        );
      })()}
    </>
  );
};

export default ChallanTab;
